import API_SERVICE from '@Service/apiService';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { getFromStorage, STORAGE_TYPE } from '@Service/storageService';
import { isAfterOrEqual } from '@Utils/versionUtils';
import { CONTRACT_SEARCH, NOTIFICATION_HANDLER } from '@Constants/types';
import store from '../store';

export const SortOrder = {
    Ascending: 'ASC',
    Descending: 'DESC',
    None: 'NONE'
};

export const SearchType = {
    Basic: 'BASIC',
    Advanced: 'ADVANCED'
};

/* Construct the basic search url */

export function basicSearchUrl(
    searchQuery,
    offset,
    pageSize,
    showMyContract,
    sortOrder,
    sortColumn
) {
    if (offset < 0) offset = 0;

    const version = API_SERVICE.getVersion();
    const rootReducer = store.getState();
    const { auth } = rootReducer;
    const { user } = auth;

    searchQuery = searchQuery.replace(/ /g, '\\ ');
    searchQuery = searchQuery.replace('*', '');
    let params = {
        offset,
        limit: pageSize,
        sort: sortColumn + ' ' + sortOrder,
        expand: 'qaHistory',
        q: searchQuery === '' ? `+Filename:*` : `+Filename:*${searchQuery}*`,
        fq: 'Deleted:false'
    };

    // +UploadedByUser:{loggedInUser}: UploadedByUserId:568c1590-e66d-438e-a860-031e3a1b3d47
    if (showMyContract)
        params.q = isAfterOrEqual('7.1.0', version)
            ? `${params.q} +UploadedByUser:{loggedInUser}`
            : `${params.q} UploadedByUserId:${user.id}`;

    return params;
}

/**
 * 
offset: 0
limit: 25
sort: Filename ASC
expand: status,downloads,notes,previousReviewers,metadata
metadata: ContractingParties,ContractClass
q: +FileType:pdf +“law” -TerminationDate:*
fq: Deleted:false AND NoContract:false
 */

/* Construct the advanced search url */
export function advancedSearchUrl(searchQuery, offset, pageSize, sortOrder, sortColumn) {
    if (offset < 0) offset = 0;

    let params = {
        offset,
        limit: pageSize,
        sort: sortColumn + ' ' + sortOrder,
        expand: 'qaHistory',
        q: searchQuery === '' ? '*' : searchQuery,
        fq: 'Deleted:false'
    };

    return params;
}

/* This endpoint determines whether or not an exported DOCX version of the contract exists.
   The resulting true/false boolean is dispatched to the reducer */
export function exportDOCXExists(documentId) {
    const endpointUrl = `/seal-ws/v5/contracts/${documentId}?expand=files`;

    return dispatch => {
        API_SERVICE.refresh();
        return API_SERVICE.SEAL_API({
            method: 'GET',
            url: endpointUrl
        })
            .then(({ status, data }) => {
                if (status === 200) {
                    let exportedDocxExists = false;
                    if (
                        data &&
                        data.files &&
                        data.files.exportedDocx &&
                        data.files.exportedDocx.href &&
                        data.files.exportedDocx.href.length > 0
                    ) {
                        exportedDocxExists = true;
                    }

                    return dispatch({
                        type: CONTRACT_SEARCH.EXPORTED_DOCX_SUCCESS,
                        payload: exportedDocxExists
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.exportDOCXExists', errMessage);

                return dispatch({
                    type: CONTRACT_SEARCH.EXPORTED_DOCX_FAILED,
                    payload: `Failed to determine if exported docx exists. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.exportDOCXExists', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.EXPORTED_DOCX_FAILED,
                    payload: `Failed to determine if exported docx exists. Error: ${err}`
                });
            });
    };
}

export function getDocxDownloadPath(documentId) {
    // if docx exists use it (response.files.exportedDocx.href !== '')
    //     path = response.files.exportedDocx.href
    // else
    // {
    //     if mimeType is a !== docx {
    //          // no exportedDocx available
    //     } else {
    //          path = response.files.original.href;
    //     }
    // }
    //
    // example path =               https://bjorn.seal-software.tech/seal-ws/v5/files/62cf13312a7f2442d3eb951e3362899c487e47b5     for exportedDocx
    // convert to download path:    https://bjorn.seal-software.tech/seal-ws/v5/downloads/62cf13312a7f2442d3eb951e3362899c487e47b5     for exportedDocx

    // /seal-ws/v5/contracts/${documentId}?expand=files
    //  response.files.original.href
    //  response.files.exportedDocx.href

    const endpointUrl = `/seal-ws/v5/contracts/${documentId}?expand=files`;

    return dispatch => {
        API_SERVICE.refresh();
        return API_SERVICE.SEAL_API({ method: 'GET', url: endpointUrl })
            .then(({ status, data }) => {
                if (status === 200) {
                    let exportedFilePath = '';
                    let originalFilePath = '';
                    if (
                        data &&
                        data.files &&
                        data.files.exportedDocx &&
                        data.files.exportedDocx.href &&
                        data.files.exportedDocx.href.length > 0
                    ) {
                        exportedFilePath = data.files.exportedDocx.href;
                    }

                    if (
                        data &&
                        data.files &&
                        data.files.original &&
                        data.files.original.href &&
                        data.files.original.href.length > 0
                    ) {
                        originalFilePath = data.files.original.href;
                    }

                    exportedFilePath = exportedFilePath.replace('/files/', '/downloads/');
                    originalFilePath = originalFilePath.replace('/files/', '/downloads/');

                    return dispatch({
                        type: CONTRACT_SEARCH.GET_DOCX_DOWNLOAD_PATH_SUCCESS,
                        payload: {
                            exportedDocx: exportedFilePath,
                            originalDocx: originalFilePath
                        }
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.getDocxDownloadPath', errMessage);

                return dispatch({
                    type: CONTRACT_SEARCH.GET_DOCX_DOWNLOAD_PATH_FAILED,
                    payload: `Failed to get Docx Download Path. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.getDocxDownloadPath', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.GET_DOCX_DOWNLOAD_PATH_FAILED,
                    payload: `Failed to get Docx Download Path. Error: ${err}`
                });
            });
    };
}

/* Generate a perma link to allow the end-user to download a contract.
   The perma link requires a nonce to authneticate the url.
   The resulting url is dispatched to the reducer */
export function generatePermaLink(documentId, type) {
    return dispatch => {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        return API_SERVICE.SEAL_API.post('/seal-ws/security/nonce', {
            service: {
                path: `/v2/files/contracts/${documentId}/${type}`
            }
        })
            .then(({ status, data }) => {
                if (status === 200) {
                    const sealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
                    const permaLink = `${sealUrl}/seal-ws/v2/files/contracts/${documentId}/${type}?nonce=${
                        data.nonce.token
                    }`;

                    return dispatch({
                        type: CONTRACT_SEARCH.GENERATE_PERMA_LINK_SUCCESS,
                        payload: { permaLink, type }
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.generatePermaLink', errMessage);
                return dispatch({
                    type: CONTRACT_SEARCH.GENERATE_PERMA_LINK_FAILED,
                    payload: `Failed to generate a nonce for document id ${documentId}. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.generatePermaLink', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.GENERATE_PERMA_LINK_FAILED,
                    payload: `Failed to generate a nonce for document id ${documentId}. Error: ${err}`
                });
            });
    };
}

/* This method fetches the contracting parties associated to the specified contract
   Use the more efficient v7.4.0 endpoint for retrieving just the party data
   Otherwise use the less efficient endpoint (that returns all the metadata) and filter the results for just the contracting party values
   The resulting contracting party list is dispatched to the reducer */

export function getContractParties(documentId) {
    const version = API_SERVICE.getVersion();
    let endpointUrl = `/seal-ws/v5/contracts/${documentId}?expand=metadata`; // version < v7.4.0
    if (isAfterOrEqual('7.4.0', version)) {
        endpointUrl = `/seal-ws/v5/contracts/${documentId}/metadata/ContractingParties`; // version >= v7.4.0
    }

    return dispatch => {
        API_SERVICE.setContentType('application/json');
        API_SERVICE.refresh();
        return API_SERVICE.SEAL_API({ method: 'GET', url: endpointUrl })
            .then(({ status, data }) => {
                if (status === 200) {
                    let parties = [];
                    if (!isAfterOrEqual('7.4.0', version)) {
                        parties = getContractingPartiesMetadata(data.metadata.items); // version < 7.4.0
                    }
                    if (isAfterOrEqual('7.4.0', version)) {
                        parties = data.values; // version >= 7.4.0
                    }

                    return dispatch({
                        type: CONTRACT_SEARCH.FETCH_CONTRACTS_PARTIES_SUCCESS,
                        payload: parties
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.getContractParties', errMessage);

                return dispatch({
                    type: CONTRACT_SEARCH.FETCH_CONTRACTS_PARTIES_FAILED,
                    payload: `Failed to fectch the contracting parties for documentId ${documentId}. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.getContractParties', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.FETCH_CONTRACTS_PARTIES_FAILED,
                    payload: `Failed to fectch the contracting parties for documentId ${documentId}. Error: ${err}`
                });
            });
    };
}
/* Filter out all metadata items except for the contractingParties metadata.
   Return just the list of party values */
export function getContractingPartiesMetadata(metadataItems) {
    try {
        metadataItems = metadataItems.filter(item => item.name === 'ContractingParties');
        if (metadataItems.length > 0 && metadataItems[0].values) {
            return metadataItems[0].values;
        }

        return [];
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'contractsActions.getContractingPartiesMetadata', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return metadataItems;
    }
}

export function clearSearch() {
    return dispatch =>
        dispatch({
            type: CONTRACT_SEARCH.SEARCH_CLEAR
        });
}

/*
This method allows the end user to naturally-type a search for a document's filename without knowing any Solr syntax
- Whatever the end-user @Constants/types will be url encoded
- A wildcard will be applied at the beginning and end of the search string (if not entered by the end-user)
- Space characters will be encoded as "\ "
- The query is fixed to the Filename metadata field
- The sort order, sort column, results paging, and mycontracts variables will be automatically appended to the url

Usage:
    contractSearch('*', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Basic)           Return 20 most recently uploaded-by-me contracts
    contractSearch('*', 60, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Basic)          Return contracts uploaded-by-me starting from 60 and ending at 80
    contractSearch('*', 0, 20, false, SortOrder.Descending, 'ModifiedAt', SearchType.Basic)          Return 20 most recently uploaded contracts
    contractSearch('', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Basic)            Return 20 most recently uploaded-by-me contracts
    contractSearch('MSA Agreement', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Basic) would translate into a solr query:    *MSA\ Agreement*     Return the 20 most recently uploaded contract by me, where the filename matches "*MSA Agreement*"

    contractSearch('id:*', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Advanced)           Return 20 most recently uploaded-by-me contracts
    contractSearch('id:*', 60, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Advanced)          Return contracts uploaded-by-me starting from 60 and ending at 80
    contractSearch('id:*', 0, 20, false, SortOrder.Descending, 'ModifiedAt', SearchType.Advanced)          Return 20 most recently uploaded contracts
    contractSearch('', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Advanced)               Return 20 most recently uploaded-by-me contracts
    contractSearch('Filename:CalPolyClean98.docx', 0, 20, true, SortOrder.Descending, 'ModifiedAt', SearchType.Advanced) would translate into a solr query:    Filename:CalPolyClean98.docx    Return the 20 most recently uploaded contract by me, where the filename = CalPolyClean98.doc
*/
export function contractSearch(
    searchQuery,
    offset,
    pageSize,
    showMyContract,
    sortOrder,
    sortColumn,
    searchType
) {
    let params = {};
    if (searchType === SearchType.Basic) {
        params = basicSearchUrl(
            searchQuery,
            offset,
            pageSize,
            showMyContract,
            sortOrder,
            sortColumn
        );
    }

    if (searchType === SearchType.Advanced) {
        params = advancedSearchUrl(searchQuery, offset, pageSize, sortOrder, sortColumn);
    }

    return dispatch => {
        dispatch({ type: CONTRACT_SEARCH.SEARCH_REQUESTED });
        API_SERVICE.refresh();
        return API_SERVICE.SEAL_API({
            method: 'GET',
            url: '/seal-ws/v5/contracts',
            params
        })
            .then(({ status, data }) => {
                if (status === 200) {
                    const { totalCount } = data.meta;

                    return dispatch({
                        type: CONTRACT_SEARCH.SEARCH_SUCCESS,
                        payload: { data, totalCount }
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.contractSearch', errMessage);

                return dispatch({
                    type: CONTRACT_SEARCH.SEARCH_FAILURE,
                    payload: `Failed to perform search with search query '${searchQuery}'. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.contractSearch', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.SEARCH_FAILURE,
                    payload: `Failed to perform search with search query '${searchQuery}'. Error: ${err}`
                });
            });
    };
}

/* The method fetches the user information object for the specified user from the server and stores
   it in the contractsReducer variable uploadedBy 
   Example userUrl: e.g. userUrl = https://basma.seal-software.net/seal-ws/v5/users/be6325ac-2231-4421-8788-67cf76643a69  */
export function getUploadedBy(userUrl) {
    return dispatch => {
        API_SERVICE.refresh();
        return API_SERVICE.SEAL_API({ method: 'GET', url: userUrl })
            .then(({ status, data }) => {
                if (status === 200) {
                    return dispatch({
                        type: CONTRACT_SEARCH.UPLOADED_BY_USER_SUCCESS,
                        payload: data.username
                    });
                }
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'contractsActions.getUploadedBy', errMessage);

                return dispatch({
                    type: CONTRACT_SEARCH.UPLOADED_BY_USER_FAILED,
                    payload: `Failed to fetch userInfo for ${userUrl}. Error code: ${status}`
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.getUploadedBy', err);
                return store.dispatch({
                    type: CONTRACT_SEARCH.UPLOADED_BY_USER_FAILED,
                    payload: `Failed to fetch userInfo for ${userUrl}. Error: ${err}`
                });
            });
    };
}

// Check if SEAL exportedDocxGeneration job is finished.  If the job has failed, extract the error details and dispatch the error message
export function checkGenerationJobStatus(jobId) {
    try {
        if (!jobId) {
            return;
        }

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        API_SERVICE.SEAL_API.get(
            `/seal-ws/v5/jobs/import-filesystem/${jobId}?expand=failedDocumentsInfo`
        )
            .then(res => {
                let trace = `checkGenerationJobStatus startedAt=${res.data.startedAt}`;
                trace = `${trace},endedAt=${res.data.endedAt}`;
                trace = `${trace},percentage=${res.data.percentage}`;
                trace = `${trace},finished=${res.data.finished}`;
                trace = `${trace},totalFiles=${res.data.totalFiles}`;
                trace = `${trace},totalDirectories=${res.data.totalDirectories}`;
                trace = `${trace},extractedDocuments=${res.data.extractedDocuments}`;
                trace = `${trace},totalFailedDocuments=${res.data.totalFailedDocuments}`;

                addLog(LOG_TYPE.INFO, 'documentActions.checkJobStatus', trace);
                // We are only sending a single document in the processing batch, therefore a single failure message in the returned json indicates an import failure
                const successfullyExtracted = !res.data.totalFailedDocuments > 0;

                // Normally, when jobStatus.finished==true the batch job has completed (irrespective of the success/failure of the document processing)
                // Note: Documents that have been sent for re-processing are in the unusual state of Finsished=false whilst PercentageComplete=100%
                //       "Re-process" is a separate API call that is made to generated the exported docx is absent, instructing the backend to re-generate this file
                if (
                    res.data.finished === true ||
                    (res.data.finished === false && res.data.percentage === '100')
                ) {
                    // batch job has completed, now check to see how successful the import was
                    if (successfullyExtracted) {
                        // Success!
                        store.dispatch({
                            type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_SUCCESS
                        });
                    } else {
                        // failedDocumentsInfo.items is an array of errors.  This ATN is only submitting a single document per batch resulting in
                        // just one error being returned (if any).  Therefore the error information is always in the zero'th position in the error array
                        const failureType = res.data.failedDocumentsInfo.items[0].type;
                        const failureMessage = res.data.failedDocumentsInfo.items[0].message;
                        const errorMessage = `${failureType}: ${failureMessage}`;

                        // dispatch the error message detailing the reason for the import failure
                        store.dispatch({
                            type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_ERROR,
                            payload: errorMessage
                        });

                        addLog(
                            LOG_TYPE.ERROR,
                            'contractsActions.checkGenerationJobStatus',
                            errorMessage
                        );

                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload: errorMessage
                        });
                    }
                }
            })
            .catch(error => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.checkGenerationJobStatus', error);

                // Don't raise jobId not found errors because this is normal when switching systems
                let skipError = false;
                if (error.response && error.response.data) {
                    const { message } = error.response.data;
                    if (message && message.length === 70) {
                        if (
                            message.startsWith('Job status for id') &&
                            message.endsWith('is not found.')
                        ) {
                            skipError = true;
                        }
                    }
                }

                // unexpected exception, dispatch error details
                if (!skipError) {
                    store.dispatch({
                        type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_ERROR,
                        payload: error
                    });
                }
            });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'contractsActions.checkGenerationJobStatus', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });

        store.dispatch({
            type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_ERROR,
            payload: err
        });
    }
}

// trigger the generation of the exported docx
export function generateExportedDocx(documentId) {
    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        const url = `/seal-ws/v5/contracts/${documentId}/files/generate?type=docx`;
        return API_SERVICE.SEAL_API.post(url)
            .then(data => {
                // get the jobId out of the response
                store.dispatch({
                    type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_REQUESTED,
                    payload: data.data.jobId
                });
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'contractsActions.generateExportedDocx', err);
                store.dispatch({
                    type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_ERROR,
                    payload: err
                });
            });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'contractsActions.generateExportedDocx', err);
        store.dispatch({
            type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_ERROR,
            payload: err
        });
        return null;
    }
}
